import { normalizeRideMode, RIDE_MODE } from "./rideMode";

export const GUEST_PREFERENCES_STORAGE_KEY = "bikepark-finder:guest-preferences";
export const GUEST_PREFERENCES_VERSION = 1;

const RADII = [25, 50, 100, 150, 200, 500];
const TYPES = ["everything", "bikepark", "trailcenter", "trail"];
const STYLES = ["downhill", "enduro", "flow", "freeride", "jump", "xc", "family"];
const ACCESS = ["lift", "shuttle", "pedal", "ebike"];
const DIFFICULTY = ["easy", "intermediate", "advanced", "expert"];
const UNITS = ["metric", "imperial"];

export const defaultGuestPreferences = () => ({
  version: GUEST_PREFERENCES_VERSION,
  discovery: {
    radius: 100,
    type: "everything",
    styles: [],
    access: [],
    difficulty: [],
    dryWeather: false,
    openNow: false,
    minTrails: 0,
    maxDrive: 0,
    minRating: 0,
  },
  rideMode: RIDE_MODE.USUAL,
  unitSystem: "metric",
});

const allowedArray = (value, allowed) => (
  Array.isArray(value) ? value.filter((item) => allowed.includes(item)) : []
);

const boundedNumber = (value, fallback, min, max, step = null) => {
  if (typeof value !== "number" || !Number.isFinite(value) || value < min || value > max) return fallback;
  if (step && Math.abs(value / step - Math.round(value / step)) > 1e-9) return fallback;
  return value;
};

export const sanitizeGuestPreferences = (value) => {
  const fallback = defaultGuestPreferences();
  if (!value || typeof value !== "object" || value.version !== GUEST_PREFERENCES_VERSION) return fallback;

  const discovery = value.discovery && typeof value.discovery === "object" ? value.discovery : {};
  return {
    version: GUEST_PREFERENCES_VERSION,
    discovery: {
      radius: RADII.includes(discovery.radius) ? discovery.radius : fallback.discovery.radius,
      type: TYPES.includes(discovery.type) ? discovery.type : fallback.discovery.type,
      styles: allowedArray(discovery.styles, STYLES),
      access: allowedArray(discovery.access, ACCESS),
      difficulty: allowedArray(discovery.difficulty, DIFFICULTY),
      dryWeather: typeof discovery.dryWeather === "boolean" ? discovery.dryWeather : fallback.discovery.dryWeather,
      openNow: typeof discovery.openNow === "boolean" ? discovery.openNow : fallback.discovery.openNow,
      minTrails: boundedNumber(discovery.minTrails, 0, 0, 12, 1),
      maxDrive: boundedNumber(discovery.maxDrive, 0, 0, 240, 15),
      minRating: boundedNumber(discovery.minRating, 0, 0, 5, 0.5),
    },
    rideMode: normalizeRideMode(value.rideMode),
    unitSystem: UNITS.includes(value.unitSystem) ? value.unitSystem : fallback.unitSystem,
  };
};

export const browserStorage = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};

export const loadGuestPreferences = (storage = browserStorage()) => {
  if (!storage) return defaultGuestPreferences();
  try {
    const raw = storage.getItem(GUEST_PREFERENCES_STORAGE_KEY);
    if (!raw) return defaultGuestPreferences();
    return sanitizeGuestPreferences(JSON.parse(raw));
  } catch {
    return defaultGuestPreferences();
  }
};

export const saveGuestPreferences = (preferences, storage = browserStorage()) => {
  if (!storage) return false;
  try {
    storage.setItem(
      GUEST_PREFERENCES_STORAGE_KEY,
      JSON.stringify(sanitizeGuestPreferences({ ...preferences, version: GUEST_PREFERENCES_VERSION })),
    );
    return true;
  } catch {
    return false;
  }
};

export const patchGuestPreferences = (patch, storage = browserStorage()) => {
  const current = loadGuestPreferences(storage);
  const next = {
    ...current,
    ...patch,
    discovery: patch.discovery ? { ...current.discovery, ...patch.discovery } : current.discovery,
    version: GUEST_PREFERENCES_VERSION,
  };
  saveGuestPreferences(next, storage);
  return sanitizeGuestPreferences(next);
};

export const resetGuestPreferences = (storage = browserStorage()) => {
  if (!storage) return false;
  try {
    storage.removeItem(GUEST_PREFERENCES_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
};
